<template>
    <div>
        <div class="mb-4 flex items-center justify-between gap-4 font-bold">
            {{ t('payments.paymentDetailsPrompt') }}

            <prime-button
                text
                class="!p-1"
                :label="t('payments.refreshPaymentForm')"
                :loading="isProcessing || isLoading"
                @click="handleRefreshPaymentForm"
            />
        </div>

        <div
            v-if="!isReady || isLoading"
            class="grid grid-cols-1 gap-4 md:grid-cols-6"
        >
            <!-- Card Number -->
            <div class="md:col-span-4">
                <div class="mb-1">{{ t('cardNumber') }}</div>
                <prime-skeleton class="min-h-[36px]" />
            </div>

            <!-- Expiration Date -->
            <div class="md:col-span-1">
                <div class="mb-1">{{ t('payments.expirationDateLabel') }}</div>
                <prime-skeleton class="min-h-[36px]" />
            </div>

            <!-- CVV -->
            <div class="md:col-span-1">
                <div class="mb-1">{{ t('securityCode') }}</div>
                <prime-skeleton class="min-h-[36px]" />
            </div>
        </div>

        <windcave-hosted-fields
            v-if="isReady && data !== undefined"
            ref="windcaveHostedFieldsRef"
            :key="sessionRenderKey"
            :payment-source-id="props.paymentSourceId"
            :payment-source-data="data"
            :is-token-session="isTokenSession"
            @payment-method="$emit('paymentMethod', $event)"
            @transaction-error="handleTransactionError"
            @validation-error="handleValidationError"
        />

        <div
            v-if="transactionError || validationError || isError"
            class="mt-4 flex items-center justify-between gap-4"
        >
            <div class="font-bold text-danger-600">
                {{ paymentFormError || '&nbsp;' }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
    PaymentSubmissionResult,
    type TokenPaymentMethod
} from '~/components/Checkout/Sections/Payment/payment-method'
import { useWindcaveScripts } from '~/components/Payments/use-windcave-scripts'
import WindcaveHostedFields from '~/components/Payments/WindcaveHostedFields.vue'
import { useMutation } from '@tanstack/vue-query'
import PrimeSkeleton from 'primevue/skeleton'
import { randomString } from '~/composables/support/util'
import PrimeButton from 'primevue/button'
import {
    useWindcaveQuery,
    type WindcaveSessionInputData
} from '~/components/Payments/use-windcave-query'
import type { PaymentMethodInput } from '~/components/Checkout/Sections/use-payment-method-input'
import type { Decimal } from '~/composables/support/decimal'
import { UserReportableError } from '~/composables/graphql'

const props = defineProps<{
    paymentSourceId: string
    checkoutId?: string
    amount?: Decimal
    input: PaymentMethodInput
    refreshButtonAboveForm?: boolean
    isProcessing?: boolean
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
    (e: 'purchaseSessionCreated', sessionId: string): void
}>()

const { t } = useI18n({ useScope: 'global' })

const { loadWindcaveHostedScripts } = useWindcaveScripts()
const isReady = ref(false)
loadWindcaveHostedScripts().then(() => {
    isReady.value = true
})

const transactionError = ref<string>()
const validationError = ref<string>()
const paymentFormError = computed(() => {
    return (
        transactionError.value ??
        validationError.value ??
        (isError.value ? t('checkoutSections.unableToLoadPaymentForm') : '')
    )
})

// Windcave purchase sessions are used for checkout payments; tokenize sessions
// are used when storing a new card for future recurring payments.
const isTokenSession = computed(() => props.amount === undefined)

const windcaveSessionInput = computed<WindcaveSessionInputData>(() => {
    // Amount greater than zero is a purchase session
    if (props.amount !== undefined) {
        // Tokenization is valid outside checkout, but every purchase session must belong to a checkout.
        if (props.checkoutId === undefined) {
            throw new Error('Windcave purchase sessions require a checkout ID')
        }

        return {
            type: 'WindcavePurchaseSessionInputData',
            checkoutId: props.checkoutId,
            amount: props.amount.toFixed(2),
            firstName: props.input.customerInformation.firstName,
            lastName: props.input.customerInformation.lastName,
            phone: props.input.customerInformation.phone,
            email: props.input.customerInformation.email,
            postalCode: props.input.addressInformation.postal
        }
    } else {
        return {
            type: 'WindcaveTokenizeSessionInputData',
            firstName: props.input.customerInformation.firstName,
            lastName: props.input.customerInformation.lastName,
            phone: props.input.customerInformation.phone,
            email: props.input.customerInformation.email,
            postalCode: props.input.addressInformation.postal
        }
    }
})

const data = ref<{
    url: string
    supportedCards: string[]
    sessionId: string
}>()
const isError = ref(false)
const sessionRenderKey = ref(randomString())
let latestSessionRequestId = 0
type WindcaveSessionRequest = {
    id: number
    paymentSourceId: string
    input: WindcaveSessionInputData
}
let pendingSessionRequest: WindcaveSessionRequest | undefined
let isDrainingSessionRequests = false
const { queryWindcaveSession } = useWindcaveQuery()
const { mutateAsync: loadWindcaveSession, isPending: isLoading } = useMutation({
    retry(failureCount, error) {
        // The GraphQL client has already shown user-reportable failures. Retry
        // only unexpected or transport failures through the server-side cache.
        return !(error instanceof UserReportableError) && failureCount < 2
    },
    mutationFn: (request: WindcaveSessionRequest) => {
        return queryWindcaveSession(request.paymentSourceId, request.input)
    }
})

async function drainWindcaveSessionRequests(): Promise<void> {
    if (isDrainingSessionRequests) {
        return
    }

    isDrainingSessionRequests = true
    try {
        while (pendingSessionRequest !== undefined) {
            // Remove the current request from the slot so changes during the await collapse to one successor.
            const request = pendingSessionRequest
            pendingSessionRequest = undefined

            try {
                const session = await loadWindcaveSession(request)
                if (request.id !== latestSessionRequestId) {
                    // The server has finished this request; drain the newer input before rendering a form.
                    continue
                }

                data.value = session
                sessionRenderKey.value = randomString()
                if (!isTokenSession.value) {
                    // Checkout preflight belongs to one hosted purchase session, so let the
                    // checkout form restore its human-check requirement for every replacement.
                    emit('purchaseSessionCreated', session.sessionId)
                }
            } catch {
                if (request.id === latestSessionRequestId) {
                    // Ignore an intermediate failure when newer input is already waiting to be provisioned.
                    isError.value = true
                }
            }
        }
    } finally {
        isDrainingSessionRequests = false
    }
}

function requestWindcaveSession(): void {
    // Only the newest desired input is retained while one server request is in flight. This keeps
    // backend session writes in the same order as the hosted form the browser will render.
    pendingSessionRequest = {
        id: ++latestSessionRequestId,
        paymentSourceId: props.paymentSourceId,
        input: { ...windcaveSessionInput.value }
    }
    data.value = undefined
    isError.value = false
    void drainWindcaveSessionRequests()
}

watch(
    [() => props.paymentSourceId, windcaveSessionInput],
    requestWindcaveSession,
    { immediate: true }
)

function handleTransactionError(message: string) {
    // Armed purchase failures return a recovery result instead; this refresh is only safe pre-arm.
    transactionError.value = message
    requestWindcaveSession()
}

function handleValidationError(message: string) {
    validationError.value = message
}

const windcaveHostedFieldsRef = ref<InstanceType<typeof WindcaveHostedFields>>()
async function submitPaymentMethod(
    beforePaymentSubmit?: () => Promise<boolean>
): Promise<PaymentSubmissionResult> {
    transactionError.value = undefined
    validationError.value = undefined
    if (windcaveHostedFieldsRef.value !== undefined) {
        // Propagate the final Windcave callback result so checkout only stops
        // before commit when validation, preflight, or payment actually failed.
        return await windcaveHostedFieldsRef.value.submitPaymentMethod(
            beforePaymentSubmit
        )
    }

    // A missing hosted form means no payment was submitted and checkout must not commit.
    return PaymentSubmissionResult.NOT_SUBMITTED
}

function getPurchaseSessionId(): string | null {
    // Purchase sessions are created while the hosted fields load, before card data is submitted.
    if (isTokenSession.value || data.value === undefined) {
        return null
    }

    return data.value.sessionId
}

function handleRefreshPaymentForm() {
    transactionError.value = undefined
    validationError.value = undefined
    requestWindcaveSession()
}

defineExpose({
    submitPaymentMethod,
    getPurchaseSessionId
})
</script>
